import React, { Component } from "react";
import { Menu, MenuItem, Button, Box, Checkbox, ListItemText } from "@mui/material";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface Item {
  key: string;
  value: string;
}

interface MultiSelectDropdownProps {
  selectedItems: string[];
  setSelectedItems: (items: string[]) => void;
  items: Item[];
  btnCustomStyle?: object;
  menuCustomStyle?: object;
  placeholder: string;
}

interface MultiSelectDropdownState {
  anchorEl: HTMLElement | null;
}

export default class MultiSelectDropdown extends Component<MultiSelectDropdownProps, MultiSelectDropdownState> {
  state: MultiSelectDropdownState = {
    anchorEl: null,
  };

  // Custom SVG icons
  CustomUncheckedIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="17" height="17" rx="5.5" stroke="#94A3B8" />
    </svg>
  );

  CustomCheckedIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="18" height="18" rx="6" fill="#652784" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M13.5548 4.88024C13.9663 5.15916 14.0737 5.71879 13.7948 6.13022L9.21865 12.8802C9.0692 13.1007 8.8298 13.2436 8.56485 13.2706C8.29989 13.2975 8.0366 13.2058 7.8458 13.02L4.32191 9.58836C3.9658 9.24158 3.95824 8.67178 4.30502 8.31568C4.6518 7.95957 5.2216 7.95201 5.5777 8.29879L8.33174 10.9807L12.3049 5.12016C12.5838 4.70873 13.1434 4.60132 13.5548 4.88024Z" fill="white" />
    </svg>
  );

  handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  handleItemSelect = (item: string) => {
    const { selectedItems, setSelectedItems } = this.props;
    if (selectedItems.includes(item)) {
      setSelectedItems(selectedItems.filter((selected) => selected !== item));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  render() {
    const { anchorEl } = this.state;
    const { 
      selectedItems, 
      items, 
      btnCustomStyle, 
      menuCustomStyle,
      placeholder,
    } = this.props;

    return (
      <Box>
        <StyledButton 
          onClick={this.handleOpen} 
          endIcon={<KeyboardArrowDownIcon sx={{ fill: "#94A3B8" }} />} 
          sx={btnCustomStyle}
        >
          {placeholder}
        </StyledButton>

        <StyledMenu
          disablePortal
          anchorEl={anchorEl} 
          open={Boolean(anchorEl)} 
          onClose={this.handleClose} 
          sx={{ 
            "& .MuiPaper-root": {
            ...menuCustomStyle,
            }
          }}
        >
          <StyledScroll>
            {items.length > 0 ? (
              items.map((item) => (
                <MenuItem 
                  key={item.key} 
                  onClick={() => this.handleItemSelect(item.value)}
                  sx={{
                    fontFamily: "Montserrat !important",
                    fontSize: "12px !important",
                    fontWeight: "500 !important",
                    lineHeight: "14.63px !important",
                    height: "36px",
                    "&:hover": {
                      backgroundColor: "#EBC3FF",
                    },
                  }}
                >
                  <Checkbox
                    icon={<this.CustomUncheckedIcon />}
                    checkedIcon={<this.CustomCheckedIcon />}
                    checked={selectedItems.includes(item.value)}
                  />
                  <ListItemText primary={item.value} />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No items available</MenuItem>
            )}
          </StyledScroll>
        </StyledMenu>
      </Box>
    );
  }
}

const StyledButton = styled(Button)({
  background: "white",
  border: "1px solid #ccc",
  borderRadius: 8,
  padding: "8px 16px",
  minWidth: "150px",
  color: "#0F172A",
  display: "flex",
  justifyContent: "space-between",
  textTransform: "none",
  fontFamily: "Montserrat !important",
  fontSize: "14px !important",
  fontWeight: "500 !important",
  lineHeight: "22px !important",
  "&:hover": { background: "#f5f5f5" },
});

const StyledMenu = styled(Menu)({
  "& .MuiPaper-root": {
    borderRadius: 8,
    border: "1px solid #CBD5E1",
    boxShadow: "none",
    minWidth: "150px",
    boxSizing: "border-box",
    paddingRight: 0,
    margin: "4px 0px",
    overflowY: "auto",
  },
});

const StyledScroll = styled(Box)({
  maxHeight: "242px",
  overflowX: "hidden",
  overflowY: "auto",
  marginRight: "7px",
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1", // ✅ Light gray color
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar": {
    width: "5px", // ✅ Make scrollbar thin
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#A1A1A1", // ✅ Slightly darker on hover
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent", // ✅ Hide scrollbar track
  },
});
